import { IExcelReportData, IRowCells, ITableData } from 'types/excelExport';
import { DEVELOPMENT_SOURCE_METRIC } from 'constants/sources';
import {
    createBlankRow,
    createDataRow,
    createHeaderRow,
    flattenUses,
    formatRateValue,
    getSourceMetricValue,
    parseNumber,
} from '../helpers';
import { EXCEL_COLORS, EXCEL_NUM_FORMATS, SOURCE_METRIC_LABELS } from '../constants';
import { LD_LABELS } from './constants';
import { ILdDevelopmentBudgetSheetParams } from './types';

const createLdUsesRow = ({
    label,
    amount,
    costPerLot,
    costPerFf,
    bold = false,
    bgColor,
    showCents = false,
}: {
    label: string;
    amount: number | null | undefined;
    costPerLot: number | null | undefined;
    costPerFf: number | null | undefined;
    bold?: boolean;
    bgColor?: string;
    showCents?: boolean;
}): ITableData => {
    const currencyFmt = showCents ? EXCEL_NUM_FORMATS.CURRENCY_CENTS : EXCEL_NUM_FORMATS.CURRENCY;
    const amountValue = amount ?? '';
    const cells: IRowCells[] = [
        { value: label, width: 30, cellFont: bold ? { bold: true } : undefined },
        {
            value: amountValue,
            width: 18,
            numFmt: typeof amountValue === 'number' ? currencyFmt : undefined,
        },
        {
            value: costPerLot ?? '',
            width: 12,
            numFmt: costPerLot != null ? currencyFmt : undefined,
        },
        {
            value: costPerFf ?? '',
            width: 12,
            numFmt: costPerFf != null ? EXCEL_NUM_FORMATS.CURRENCY_CENTS : undefined,
        },
    ];
    const row = createDataRow(cells, bold);
    if (bgColor) {
        row.rowFill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: bgColor },
        };
    }
    return row;
};

const createLdSourceRow = (
    label: string,
    amount: number | null | undefined,
    percentOfTotal: number | null | undefined,
    loanRateType: string | null | undefined,
    fixedRate: number | null | undefined,
    metricValue: number | null | undefined,
    bold: boolean = false,
    bgColor?: string,
    showCents: boolean = false,
): ITableData => {
    const currencyFmt = showCents ? EXCEL_NUM_FORMATS.CURRENCY_CENTS : EXCEL_NUM_FORMATS.CURRENCY;
    const rateValue = formatRateValue(loanRateType, fixedRate);
    const isRateNumeric = loanRateType !== 'variable' && fixedRate != null;
    const cells: IRowCells[] = [
        { value: label, width: 30, cellFont: bold ? { bold: true } : undefined },
        { value: amount ?? 0, width: 18, numFmt: currencyFmt },
        { value: percentOfTotal ?? 0, width: 12, numFmt: EXCEL_NUM_FORMATS.PERCENT },
        {
            value: isRateNumeric ? fixedRate : rateValue,
            width: 12,
            numFmt: isRateNumeric ? EXCEL_NUM_FORMATS.PERCENT : undefined,
        },
        { value: metricValue ?? 0, width: 12, numFmt: EXCEL_NUM_FORMATS.PERCENT },
    ];
    const row = createDataRow(cells, bold);
    if (bgColor) {
        row.rowFill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: bgColor },
        };
    }
    return row;
};

export const formatLdDevelopmentBudgetSheet = ({
    dealName,
    proForma,
    usesSummary,
    sourcesSummary,
    showCents = false,
}: ILdDevelopmentBudgetSheetParams): IExcelReportData => {
    const labels = LD_LABELS.developmentBudget;
    const today = new Date().toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
    });
    const totalFrontFootage = parseNumber(proForma.totalFrontFootage) ?? 0;

    const computeCostPerFf = (amount: number | null | undefined): number | null => {
        if (amount == null || totalFrontFootage === 0) return null;
        return amount / totalFrontFootage;
    };

    const topHeaderRows: ITableData[] = [
        createDataRow([{ value: dealName, cellFont: { bold: true, size: 14 }, width: 30 }]),
        createDataRow([{ value: labels.title, cellFont: { bold: true, size: 14 }, width: 30 }]),
        createDataRow([{ value: today, cellFont: { bold: true, size: 14 }, width: 30 }]),
        createBlankRow(4),
    ];

    const usesHeaderRow = createHeaderRow(
        [labels.usesHeader, labels.amount, labels.costPerLot, labels.costPerFf],
        EXCEL_COLORS.BLUE_HEADER,
        true,
    );
    const usesColCount = 4;
    const usesRows: ITableData[] = [];

    if (usesSummary?.uses) {
        const flattenedUses = flattenUses(usesSummary.uses);
        flattenedUses.forEach(({ use, level }, index) => {
            const hasChildren = Boolean(use.children && use.children.length > 0);
            const isCategory = level === 0 || hasChildren;
            const displayName = hasChildren
                ? (use.description || use.nodeName || '')
                : (use.budgetLineNickname || use.nodeName || '');

            const nextEntry = flattenedUses[index + 1];
            const nextHasChildren = Boolean(nextEntry?.use.children?.length);
            const nextDisplayName = nextHasChildren
                ? (nextEntry.use.description || nextEntry.use.nodeName || '')
                : undefined;
            const isDuplicateOfNext = hasChildren && nextDisplayName === displayName;

            let bgColor: string | undefined;
            if (level === 0) bgColor = EXCEL_COLORS.DARK_GRAY;
            else if (hasChildren) bgColor = EXCEL_COLORS.GRAY_HEADER;

            if (!isDuplicateOfNext) {
                const amount = parseNumber(use.amount);
                usesRows.push(createLdUsesRow({
                    label: displayName,
                    amount,
                    costPerLot: parseNumber(use.costPerLot),
                    costPerFf: computeCostPerFf(amount),
                    bold: isCategory,
                    bgColor,
                    showCents,
                }));
            }

            const isLastRow = index === flattenedUses.length - 1;
            if (level === 0 && !isLastRow) {
                usesRows.push(createBlankRow(usesColCount));
            }
        });
    }

    const totalAmount = parseNumber(usesSummary?.totals?.amount);
    usesRows.push(createBlankRow(usesColCount));
    usesRows.push(createLdUsesRow({
        label: labels.totalDevelopmentCost,
        amount: totalAmount,
        costPerLot: parseNumber(usesSummary?.totals?.costPerLot),
        costPerFf: computeCostPerFf(totalAmount),
        bold: true,
        bgColor: EXCEL_COLORS.DARK_GRAY,
        showCents,
    }));
    usesRows.push(createBlankRow(usesColCount));
    usesRows.push(createBlankRow(usesColCount));

    const firstSourceWithMetric = sourcesSummary?.sources?.find(s => s.metricType);
    const metricType = firstSourceWithMetric?.metricType || DEVELOPMENT_SOURCE_METRIC.LTC;
    const metricLabel = SOURCE_METRIC_LABELS[metricType] || 'LTC';

    const sourcesHeaderRow = createHeaderRow(
        [labels.sourcesHeader, labels.amount, labels.percentOfTotal, labels.rateTerms, metricLabel],
        EXCEL_COLORS.BLUE_HEADER,
        true,
    );

    const sourcesRows: ITableData[] = [];
    sourcesSummary?.sources?.forEach(source => {
        sourcesRows.push(createLdSourceRow(
            source.name,
            parseNumber(source.amount),
            parseNumber(source.sourcesPercentage),
            source.loanRateType,
            parseNumber(source.fixedLoanRate),
            getSourceMetricValue(source),
            false,
            undefined,
            showCents,
        ));
    });

    sourcesRows.push(createLdSourceRow(
        labels.totalSources,
        parseNumber(sourcesSummary?.totals?.amount),
        1,
        null,
        null,
        null,
        true,
        EXCEL_COLORS.DARK_GRAY,
        showCents,
    ));

    return {
        topHeaderRows,
        reportTables: [
            { tableHeaderRows: [usesHeaderRow], tableData: usesRows },
            { tableHeaderRows: [sourcesHeaderRow], tableData: sourcesRows },
        ],
    };
};
